import React from 'react';
import PropTypes from 'prop-types';

import multiplyIcon from 'core/assets/svg/ic-multiply-16px.svg';
import Divider from 'core/components/divider';
import { Label } from 'core/components/label';
import { numberFormatter } from 'core/helpers/formatters';
import { InlineCallout } from 'quote-revisit/components/inline-callout';
import { Slot, hasSlotFills, PolicyCardSlot } from '../slots';
import { getPriceDetails, PriceDetailsVariant } from './get-price-details';
import useStyles from './price-details.styles';

const Amount = ({ amount }) => {
  const classes = useStyles();
  return (
    <span className={classes.amount}>
      <Label type="body2" component="span" className={classes.currency}>
        $
      </Label>
      <Label type="body2" component="span" className={classes.amountValue}>
        {numberFormatter(amount, 2)}
      </Label>
    </span>
  );
};

Amount.propTypes = {
  amount: PropTypes.number.isRequired
};

const Payments = ({ count, amount }) => {
  const classes = useStyles();
  return (
    <span className={classes.payments}>
      <Label type="body2" component="span" className={classes.amountValue}>
        {count}
      </Label>
      <img src={multiplyIcon} alt="times" className={classes.multiply} />
      <Amount amount={amount} />
    </span>
  );
};

Payments.propTypes = {
  count: PropTypes.number.isRequired,
  amount: PropTypes.number.isRequired
};

const PriceRow = ({ label, children }) => {
  const classes = useStyles();
  return (
    <div className={classes.row}>
      <Label type="body2" className={classes.rowLabel}>
        {label}
      </Label>
      {children}
    </div>
  );
};

PriceRow.propTypes = {
  label: PropTypes.string.isRequired,
  children: PropTypes.node.isRequired
};

/**
 * The installments section of a monthly price box: the payments row, then the billing day row (#36361's
 * slot) and the close-payments callout when they apply.
 */
const Installments = ({ label, priceDetails, policy }) => (
  <>
    <PriceRow label={label}>
      <Payments count={priceDetails.paymentCount} amount={priceDetails.paymentAmount} />
    </PriceRow>
    <Slot name={PolicyCardSlot.BillingDay} policy={policy} />
    {priceDetails.daysToSecondPayment != null && (
      <InlineCallout>
        Heads up — 2nd payment in{' '}
        <strong>{priceDetails.daysToSecondPayment === 1 ? '1 day' : `${priceDetails.daysToSecondPayment} days`}</strong>
        .
      </InlineCallout>
    )}
  </>
);

Installments.propTypes = {
  label: PropTypes.string.isRequired,
  priceDetails: PropTypes.shape({
    paymentCount: PropTypes.number.isRequired,
    paymentAmount: PropTypes.number.isRequired,
    daysToSecondPayment: PropTypes.number
  }).isRequired,
  policy: PropTypes.object.isRequired
};

/**
 * The policy card's price details (#36357), in the variant that fits how the policy is paid: billed to
 * escrow, one-time, monthly with even payments, or monthly with a different payment due at bind.
 */
const PriceDetails = ({ policy }) => {
  const classes = useStyles();
  const priceDetails = getPriceDetails(policy);
  if (!priceDetails) {
    return null;
  }

  if (priceDetails.variant === PriceDetailsVariant.Escrow || priceDetails.variant === PriceDetailsVariant.OneTime) {
    return (
      <div className={classes.priceDetails}>
        <Label type="body1" component="h4" className={classes.heading}>
          Price details
        </Label>
        <div className={classes.box}>
          <PriceRow label={priceDetails.variant === PriceDetailsVariant.Escrow ? 'Billed to escrow' : 'Due at bind'}>
            <Amount amount={priceDetails.amount} />
          </PriceRow>
        </div>
      </div>
    );
  }

  // Sections of the monthly box, with a rule between each pair.
  const sections =
    priceDetails.variant === PriceDetailsVariant.EvenPayments
      ? [<Installments key="payments" label="Payments" priceDetails={priceDetails} policy={policy} />]
      : [
          <React.Fragment key="due-at-bind">
            <PriceRow label="Due at bind">
              <Amount amount={priceDetails.dueAtBindAmount} />
            </PriceRow>
            {priceDetails.isCombined && <InlineCallout>This includes the 1st and 2nd payments</InlineCallout>}
          </React.Fragment>,
          <Installments key="then" label="Then" priceDetails={priceDetails} policy={policy} />
        ];
  if (hasSlotFills(PolicyCardSlot.PaymentScheduleLink)) {
    sections.push(
      <div key="payment-schedule" className={classes.paymentScheduleLink}>
        <Slot name={PolicyCardSlot.PaymentScheduleLink} policy={policy} />
      </div>
    );
  }

  return (
    <div className={classes.priceDetails}>
      <Label type="body1" component="h4" className={classes.heading}>
        Price details
      </Label>
      <div className={classes.box}>
        {sections.map((section, index) => (
          <React.Fragment key={section.key}>
            {index > 0 && <Divider className={classes.divider} />}
            <div className={classes.section}>{section}</div>
          </React.Fragment>
        ))}
      </div>
      {priceDetails.termTotal != null && (
        <div className={classes.termTotal}>
          <Label type="body2" className={classes.rowLabel}>
            Total term price
          </Label>
          <Label type="body2" className={classes.termTotalValue}>
            ${numberFormatter(priceDetails.termTotal, 2)}
          </Label>
        </div>
      )}
    </div>
  );
};

PriceDetails.propTypes = {
  policy: PropTypes.shape({
    paymentMethod: PropTypes.string,
    paymentType: PropTypes.string,
    total: PropTypes.number,
    schedule: PropTypes.array,
    termMonths: PropTypes.number.isRequired
  }).isRequired
};

export default PriceDetails;
